<script setup lang="ts">
import mexBanksData from '@/assets/json/mex-banks.json';
import type { MexBankAccountData } from '@/types/UserData';

type Props = {
  showSaveButton?: boolean;
  opacity?: boolean;
};

const { showSaveButton = true, opacity = false } = defineProps<Props>();

const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { userMexBankAccount } = storeToRefs(useUserDataStore());
const { getUserMexBankAccount, updateUserMexBankAccount } = useUserDataStore();
const { userHasDocument, userDocument } = useUserData();
const { openDepositModal } = usePayments();

const isLoading = ref<boolean>(false);

const accountTypeInitial = computed(() => {
  return userMexBankAccount.value.type || 'clabe';
});

const bankCodeInitial = computed(() => {
  return userMexBankAccount.value.bank_code || '';
});

const accountNumberInitial = computed(() => {
  return userMexBankAccount.value.number || '';
});

const formFields = ref<MexBankAccountData>({
  type: accountTypeInitial.value,
  bank_code: bankCodeInitial.value,
  number: accountNumberInitial.value,
});

async function onBankAccountFormSubmit() {
  if (!formFields.value.type || !formFields.value.number) {
    openToaster({
      message: t('account.user_data.errors.empty_field'),
      type: 'error',
    });

    return;
  }

  isLoading.value = true;

  await updateUserMexBankAccount(formFields.value)
    .then((res) => {
      if (!res.data.bank_account.number) {
        const error_msg = res.data.error
          ? res.data.error
          : res.data.message
            ? res.data.message
            : t('account.user_data.errors.bank_error');

        openToaster({
          message: error_msg,
          type: 'error',
        });

        return;
      }

      openToaster({
        message: t('account.user_data.update_bank_success'),
        type: 'success',
      });

      isEditing.value = false;
    })
    .catch((err) => {
      let error_msg = t('account.user_data.errors.bank_error');

      if (err.data.error) {
        error_msg = err.data.error;
      }
      if (err.data.message) {
        error_msg = err.data.message;
      }
      if (err.data.number) {
        error_msg = err.data.number;
      }
      if (err.data.type) {
        error_msg = err.data.type;
      }
      if (err.data.bank_code) {
        error_msg = err.data.bank_code;
      }

      openToaster({
        message: error_msg,
        type: 'error',
      });
    })
    .finally(() => {
      isLoading.value = false;
    });
}

onMounted(() => {
  if (!userMexBankAccount.value.number) {
    getUserMexBankAccount();
  }
});

watch(
  () => formFields.value.type,
  (type) => {
    if (type === 'clabe') {
      formFields.value.bank_code = '';
    }
  },
);

const selectedBankType = computed(() => formFields.value.type);
const selectedBankCode = computed(() => formFields.value.bank_code);
const selectedBankNumber = computed(() => formFields.value.number);

const hasPendency = ref<boolean>(false);

const isEditing = ref<boolean>(false);

const toggleEdit = () => {
  isEditing.value = !isEditing.value;
};

defineExpose({
  name: 'clabe',
  selectedBankType,
  selectedBankCode,
  selectedBankNumber,
  hasPendency,
  toggleEdit,
  isEditing,
});
</script>

<template>
  <ClientOnly>
    <BaseLoading
      class="!bg-auth-bg-primary"
      :icon-loader="true"
      :show="isLoading"
    />

    <div :class="[$style.toggleBox, { [$style.opacity]: opacity }]">
      <div :class="$style.header">
        <div :class="$style.title">
          {{ $t('account.user_data.payment_my_account') }}
        </div>
        <button
          :class="$style.edit"
          @click.prevent="toggleEdit"
        >
          {{ isEditing ? $t('general.cancel') : $t('general.edit') }}
        </button>
      </div>

      <div
        v-if="!isEditing"
        :class="$style.viewContent"
      >
        <template v-if="accountTypeInitial && accountNumberInitial">
          <div class="inline-flex items-center gap-2">
            {{ accountTypeInitial.toUpperCase() }}:
            {{ bankCodeInitial }}
            {{ accountNumberInitial }}
          </div>
        </template>

        <template v-else>
          <span
            class="text-primary text-xs cursor-pointer"
            @click="toggleEdit"
          >
            {{ $t('account.user_data.incomplete_bank_account') }}
          </span>
        </template>
      </div>

      <div
        v-else
        :class="$style.editContent"
      >
        <template v-if="userHasDocument">
          <form @submit.prevent="onBankAccountFormSubmit">
            <div :class="$style.componentBox">
              <div
                class="md:max-w-48"
                :class="$style.formGroup"
              >
                <label for="account_type">
                  {{ $t('account.user_data.mex.account_type') }}
                </label>
                <BaseFormSelectField
                  id="account_type"
                  v-model="formFields.type"
                  :initial-value="accountTypeInitial"
                  name="account_type"
                  :options="[
                    { value: 'clabe', label: 'CLABE' },
                    { value: 'debit', label: 'Débito' },
                    { value: 'phone', label: 'Telefone' },
                  ]"
                />
              </div>

              <div
                v-if="formFields.type !== 'clabe'"
                class="md:max-w-64 md:min-w-60"
                :class="$style.formGroup"
              >
                <label for="bank_code">
                  {{ $t('account.user_data.mex.bank_code') }}
                </label>
                <BaseFormSelectField
                  id="bank_code"
                  v-model="formFields.bank_code"
                  :initial-value="bankCodeInitial"
                  name="bank_code"
                  :options="mexBanksData"
                />
              </div>

              <div
                class="md:max-w-full md:min-w-60"
                :class="$style.formGroup"
              >
                <label for="account_number">
                  {{ $t('account.user_data.mex.account_number') }}
                </label>

                <BaseFormInput
                  id="account_number"
                  v-model="formFields.number"
                  autocomplete="off"
                  :initial-value="accountNumberInitial"
                  name="account_number"
                  :placeholder="$t('account.user_data.mex.account_number')"
                  type="text"
                />
              </div>
            </div>

            <div
              :class="$style.thirdAlert"
              v-html="$t('withdraw.spei.account_alert', { doc: userDocument })"
            />

            <div
              v-if="showSaveButton"
              class="flex justify-between items-end w-full col-span-3 mt-3"
            >
              <button
                :class="$style.btnSubmit"
                type="submit"
              >
                <NuxtIcon name="check" />
                {{ $t('account.save') }}
              </button>
            </div>
          </form>
        </template>

        <template v-else>
          <div
            :class="$style.makeYourDeposit"
            @click="openDepositModal()"
          >
            <span class="cursor-pointer">
              {{ $t('account.user_data.make_your_deposit') }}
            </span>
            <button :class="$style.btnDeposit">
              {{ $t('header.deposit') }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.toggleBox {
  @apply flex flex-col gap-3 p-1 md:p-0 px-1 md:px-2;

  &.opacity {
    @apply opacity-20 pointer-events-none select-none;
  }

  .header {
    @apply flex items-center justify-between;

    .title {
      @apply text-sm font-semibold;
    }

    .pill {
      @apply bg-texts/20 text-white px-1.5 py-0.5 rounded-full text-[.6rem] font-semibold leading-3 mr-auto ml-2;

      &.redPill {
        @apply bg-error;
      }

      &.greenPill {
        @apply bg-success;
      }
    }

    .edit {
      @apply text-2xs md:text-xs font-bold uppercase opacity-60 px-1;
    }
  }

  .viewContent {
    @apply text-sm md:text-base text-texts/80;

    .verifyBtn {
      @apply text-xs text-primary mt-3 text-left;
    }
  }

  .editContent {
    @apply w-full;

    .description {
      @apply text-sm md:text-sm text-texts/70 mb-4 max-w-[540px];
    }
  }
}

.componentBox {
  @apply w-full flex items-start justify-start flex-wrap gap-2 md:gap-4;

  .formGroup {
    @apply flex-1 w-full relative min-w-[50%] md:min-w-[20%];

    label {
      @apply text-xs font-medium;
    }
  }
}

.thirdAlert {
  @apply text-warning text-xs mb-2 px-0.5 w-full mt-3;

  strong {
    @apply font-bold whitespace-nowrap;
  }
}

.btnSubmit {
  @apply bg-button-bg text-button-text py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2;
}

.makeYourDeposit {
  @apply flex flex-col md:flex-row justify-center md:justify-between items-center gap-2 border border-dotted border-texts/40 text-texts/50 rounded-lg py-5 px-6 cursor-pointer text-center md:text-left;

  .btnDeposit {
    @apply bg-button-bg text-button-text font-semibold px-4 py-2 rounded-md text-sm cursor-pointer;
  }
}
</style>
